import { useMemo } from 'react';
import { ALL_SERVICES, ServiceOption, TranslationType } from '../constants/services';
import { SelectedProject } from '../types';

const getServicesByType = (
    type: TranslationType,
    predicate?: (service: ServiceOption) => boolean
): ServiceOption[] => {
    const byType = ALL_SERVICES.filter(service => service.applicableTypes.includes(type));
    return predicate ? byType.filter(predicate) : byType;
};

interface UseProjectServicesReturn {
    includedAddons: ServiceOption[];
    availableServices: ServiceOption[];
    typeAddons: ServiceOption[];
    translationType: TranslationType;
    crossTypeAvailableServices: ServiceOption[];
    crossTranslationType: TranslationType;
}

export const useProjectServices = (
    selectedProject: SelectedProject | null
): UseProjectServicesReturn => {
    const translationType = (selectedProject?.order?.translation_type || 'certified') as TranslationType;

    const crossTranslationType: TranslationType = translationType === 'certified' ? 'standard' : 'certified';

    const typeAddons = useMemo<ServiceOption[]>(() => {
        return getServicesByType(translationType);
    }, [translationType]);

    const includedAddons = useMemo<ServiceOption[]>(() => {
        if (!selectedProject?.order) return [];

        const order = selectedProject.order as Record<string, unknown>;

        return getServicesByType(translationType, (service) => {
            const keys = service.keys?.length ? service.keys : [service.key];
            return keys.some((k) => order[k] === '1');
        });
    }, [selectedProject?.order, translationType]);

    const availableServices = useMemo<ServiceOption[]>(() => {
        if (!selectedProject?.order) return [];

        const order = selectedProject.order as Record<string, unknown>;

        return getServicesByType(translationType, (service) => {
            const keys = service.keys?.length ? service.keys : [service.key];
            return keys.every((k) => order[k] !== '1');
        });
    }, [selectedProject?.order, translationType]);

    const crossTypeAvailableServices = useMemo<ServiceOption[]>(() => {
        return getServicesByType(
            crossTranslationType,
            (service) => service.key !== 'include_expedited_turnaround'
        );
    }, [crossTranslationType]);

    return {
        includedAddons,
        availableServices,
        typeAddons,
        translationType,
        crossTypeAvailableServices,
        crossTranslationType
    };
};
